import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Lightbulb,
  Sparkles,
  Bug,
  Edit,
  Trash2,
  ArrowRightCircle,
  FileText,
  User,
  Calendar,
  X,
  Plus,
  Package,
  MessageSquare,
  Send,
  Loader2,
  Reply,
  CornerDownRight,
  Bell,
  Mail,
  Users,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PageTransition } from '../../components/animations/PageTransition';
import { HtmlRenderer, RichTextEditor } from '../../components/editor';
import {
  REQUEST_STATUSES,
  REQUEST_TYPES,
  REQUEST_PRIORITIES,
  REQUEST_STATUS_FLOW,
} from '@yoda/shared';
import type { RequestStatus } from '@yoda/shared';
import { useUIStore } from '../../stores/uiStore';

interface RequestComment {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string | null;
  content: string | null;
  contentHtml: string | null;
  isInternal: boolean;
  parentCommentId: string | null;
  createdAt: string;
}

interface RequestDetail {
  id: string;
  projectId: string;
  title: string;
  descriptionHtml: string | null;
  type: 'idea' | 'feature' | 'bug';
  status: RequestStatus;
  priority: string | null;
  createdBy: string;
  assignedTo: string | null;
  reviewedBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  convertedToWorkItemId: string | null;
  convertedToBundleId: string | null;
  mergedIntoRequestId: string | null;
  createdAt: string;
  updatedAt: string;
  project: { id: string; name: string };
  creator: { id: string; name: string; email: string; avatarUrl: string | null };
  assignee: { id: string; name: string; email: string; avatarUrl: string | null } | null;
  reviewer: { id: string; name: string; avatarUrl: string | null } | null;
  approver: { id: string; name: string; avatarUrl: string | null } | null;
  convertedWorkItem: { id: string; title: string } | null;
  convertedBundle: { id: string; title: string; status: string } | null;
  tags: { id: string; name: string; color: string }[];
  commentCount: number;
  comments: RequestComment[];
}

interface TagItem {
  id: string;
  name: string;
  color: string;
}

const typeIcons = {
  idea: Lightbulb,
  feature: Sparkles,
  bug: Bug,
};

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setSidebarCollapsed } = useUIStore();

  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', descriptionHtml: '' });
  const [saving, setSaving] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [allTags, setAllTags] = useState<TagItem[]>([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planDescription, setPlanDescription] = useState('');
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ id: string; userName: string; content: string } | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Collapse sidebar when editing
  useEffect(() => {
    if (isEditing || showPlanModal) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
    return () => setSidebarCollapsed(false);
  }, [isEditing, showPlanModal, setSidebarCollapsed]);

  useEffect(() => {
    if (id) {
      fetchRequest();
      fetchTags();
    }
  }, [id]);

  const fetchRequest = async () => {
    setLoading(true);
    try {
      const response = await api.get<RequestDetail>(`/requests/${id}`);
      const data = response.data;
      if (data) {
        setRequest(data);
        setEditForm({
          title: data.title,
          descriptionHtml: data.descriptionHtml || '',
        });
        setPlanDescription(data.descriptionHtml || '');
      }
    } catch (error) {
      console.error('Failed to fetch request:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get<TagItem[]>('/tags');
      setAllTags(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleSaveEdit = async () => {
    if (!request) return;
    setSaving(true);
    try {
      await api.patch(`/requests/${request.id}`, {
        title: editForm.title,
        descriptionHtml: editForm.descriptionHtml,
      });
      setRequest({ ...request, ...editForm });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!request) return;
    try {
      await api.patch(`/requests/${request.id}/status`, { status: newStatus });
      setRequest({ ...request, status: newStatus as RequestStatus });
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    // Strip HTML tags to check if there's actual content
    const plainText = newComment.replace(/<[^>]*>/g, '').trim();
    if (!request || !plainText) return;

    try {
      setIsSubmittingComment(true);
      await api.post(`/requests/${request.id}/comments`, {
        content: plainText,
        contentHtml: newComment,
      });
      setNewComment('');
      setShowCommentForm(false);
      // Refetch request to get updated comments
      fetchRequest();
    } catch (error) {
      console.error('Failed to submit comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    const plainText = replyContent.replace(/<[^>]*>/g, '').trim();
    if (!request || !replyingTo || !plainText) return;

    try {
      setIsSubmittingComment(true);
      await api.post(`/requests/${request.id}/comments`, {
        content: plainText,
        contentHtml: replyContent,
        parentCommentId: replyingTo.id,
      });
      setReplyContent('');
      setReplyingTo(null);
      fetchRequest();
    } catch (error) {
      console.error('Failed to submit reply:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const startReply = (comment: RequestComment) => {
    const preview = comment.contentHtml
      ? comment.contentHtml.replace(/<[^>]*>/g, '').substring(0, 100)
      : (comment.content || '').substring(0, 100);
    setReplyingTo({ id: comment.id, userName: comment.userName, content: preview });
    setReplyContent('');
  };

  const handleSendNotification = async (notificationType: 'new_request' | 'plan_proposed' | 'status_change' | 'notify_all_clients') => {
    if (!request) return;
    setSendingNotification(true);
    setNotificationMessage(null);
    try {
      const response = await api.post<{ message: string }>(`/requests/${request.id}/notify`, { notificationType });
      setNotificationMessage({ type: 'success', text: response.data?.message || 'Notification sent successfully!' });
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error: any) {
      setNotificationMessage({ type: 'error', text: error.message || 'Failed to send notification' });
    } finally {
      setSendingNotification(false);
    }
  };

  const handleProposePlan = async () => {
    if (!request) return;
    setSaving(true);
    try {
      await api.post(`/requests/${request.id}/propose-plan`, {
        descriptionHtml: planDescription,
      });
      setRequest({
        ...request,
        descriptionHtml: planDescription,
        status: 'plan_proposed',
      });
      setShowPlanModal(false);
    } catch (error) {
      console.error('Failed to propose plan:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToWorkItem = async () => {
    if (!request || !window.confirm('Convert this request to a work item?')) return;
    try {
      await api.post(`/requests/${request.id}/convert`, { type: 'work_item' });
      fetchRequest();
    } catch (error) {
      console.error('Failed to convert:', error);
    }
  };

  const handleConvertToBundle = async () => {
    if (!request || !window.confirm('Convert this request to a bundle?')) return;
    try {
      const response = await api.post<{ bundleId: string }>(`/requests/${request.id}/convert`, { type: 'bundle' });
      navigate(`/bundles/${response.data?.bundleId}`);
    } catch (error) {
      console.error('Failed to convert:', error);
    }
  };

  const handleAddTag = async (tagId: string) => {
    if (!request) return;
    try {
      await api.post(`/tags/requests/${request.id}/tags`, { tagId });
      const tag = allTags.find((t) => t.id === tagId);
      if (tag) {
        setRequest({ ...request, tags: [...request.tags, tag] });
      }
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!request) return;
    try {
      await api.delete(`/tags/requests/${request.id}/tags/${tagId}`);
      setRequest({
        ...request,
        tags: request.tags.filter((t) => t.id !== tagId),
      });
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleDelete = async () => {
    if (!request || !window.confirm('Delete this request?')) return;
    try {
      await api.delete(`/requests/${request.id}`);
      navigate('/requests');
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="space-y-6">
          <div className="h-8 w-48 bg-bg-secondary rounded animate-pulse" />
          <div className="h-64 bg-bg-secondary rounded-2xl animate-pulse" />
        </div>
      </PageTransition>
    );
  }

  if (!request) {
    return (
      <PageTransition>
        <div className="text-center py-12">
          <p className="text-text-secondary">Request not found</p>
          <Link to="/requests" className="text-accent hover:underline mt-2 inline-block">
            Back to Requests
          </Link>
        </div>
      </PageTransition>
    );
  }

  const TypeIcon = typeIcons[request.type];
  const typeInfo = REQUEST_TYPES[request.type];
  const statusInfo = REQUEST_STATUSES[request.status];
  const priorityInfo = request.priority
    ? REQUEST_PRIORITIES[request.priority as keyof typeof REQUEST_PRIORITIES]
    : null;
  const allowedTransitions = REQUEST_STATUS_FLOW[request.status] || [];
  const unusedTags = allTags.filter((t) => !request.tags.find((rt) => rt.id === t.id));

  // Edit mode - use same layout as new request page
  if (isEditing) {
    return (
      <PageTransition>
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Edit Request</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} isLoading={saving}>
                Save
              </Button>
            </div>
          </div>

          {/* Form - same layout as new request page */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0">
            {/* Left Column - Title + Editor (5/6) */}
            <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
              {/* Title */}
              <div className="mb-3">
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Request title..."
                  className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
              </div>
              {/* Editor */}
              <div className="flex-1 min-h-0">
                <RichTextEditor
                  content={editForm.descriptionHtml}
                  onChange={(html) => setEditForm({ ...editForm, descriptionHtml: html })}
                  placeholder="Describe the request in detail. You can paste images and annotate them."
                  className="h-full"
                />
              </div>
            </div>

            {/* Right Column - Info (1/6) */}
            <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <div
                  className="flex items-center gap-2 p-2.5 rounded-xl"
                  style={{ backgroundColor: typeInfo.bgColor }}
                >
                  <TypeIcon className="w-4 h-4" style={{ color: typeInfo.color }} />
                  <span className="font-medium text-sm" style={{ color: typeInfo.color }}>
                    {typeInfo.label}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <span
                  className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: statusInfo?.bgColor,
                    color: statusInfo?.color,
                  }}
                >
                  {statusInfo?.label || request.status}
                </span>
              </div>

              {/* Priority */}
              {priorityInfo && (
                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <span
                    className="inline-block px-3 py-1.5 rounded-lg text-sm"
                    style={{
                      backgroundColor: priorityInfo.bgColor,
                      color: priorityInfo.color,
                    }}
                  >
                    {priorityInfo.label}
                  </span>
                </div>
              )}

              {/* Project */}
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <p className="text-sm text-text-secondary">{request.project.name}</p>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Propose Plan mode - full screen editor
  if (showPlanModal) {
    return (
      <PageTransition>
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowPlanModal(false)}
                className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Propose Plan</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setShowPlanModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleProposePlan} isLoading={saving}>
                Propose Plan
              </Button>
            </div>
          </div>

          {/* Form */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0">
            {/* Left Column - Title + Editor (5/6) */}
            <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
              {/* Title (read-only) */}
              <div className="mb-3">
                <input
                  type="text"
                  value={request.title}
                  readOnly
                  className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-lg opacity-60 cursor-not-allowed"
                />
              </div>
              {/* Editor */}
              <div className="flex-1 min-h-0">
                <RichTextEditor
                  content={planDescription}
                  onChange={setPlanDescription}
                  placeholder="Rewrite or clarify the description to create a clear plan for the client to approve..."
                  className="h-full"
                />
              </div>
            </div>

            {/* Right Column - Info (1/6) */}
            <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
              <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl">
                <FileText className="w-5 h-5 text-violet-500 mb-2" />
                <p className="text-sm text-text-secondary">
                  Edit the description to clarify scope, deliverables, and timeline. The client will review and approve this plan.
                </p>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <div
                  className="flex items-center gap-2 p-2.5 rounded-xl"
                  style={{ backgroundColor: typeInfo.bgColor }}
                >
                  <TypeIcon className="w-4 h-4" style={{ color: typeInfo.color }} />
                  <span className="font-medium text-sm" style={{ color: typeInfo.color }}>
                    {typeInfo.label}
                  </span>
                </div>
              </div>

              {/* Current Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Current Status</label>
                <span
                  className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: statusInfo?.bgColor,
                    color: statusInfo?.color,
                  }}
                >
                  {statusInfo?.label || request.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Normal view mode
  return (
    <PageTransition>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: typeInfo.bgColor }}
            >
              <TypeIcon className="w-6 h-6" style={{ color: typeInfo.color }} />
            </div>

            <div>
              <h1 className="text-2xl font-bold">{request.title}</h1>
              <div className="flex items-center gap-2 text-text-secondary">
                <Link to={`/projects/${request.projectId}`} className="hover:text-accent">
                  {request.project.name}
                </Link>
                <span>•</span>
                <span>{typeInfo.label}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <h2 className="text-lg font-semibold mb-4">Description</h2>
              <HtmlRenderer html={request.descriptionHtml || ''} />
            </div>

            {/* Notification Actions */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="w-5 h-5 text-text-secondary" />
                <h2 className="text-lg font-semibold">Notifications</h2>
              </div>

              {notificationMessage && (
                <div className={`p-3 rounded-xl mb-4 ${
                  notificationMessage.type === 'success'
                    ? 'bg-green-500/10 border border-green-500/20 text-green-500'
                    : 'bg-red-500/10 border border-red-500/20 text-red-500'
                }`}>
                  {notificationMessage.text}
                </div>
              )}

              <div className="space-y-3">
                {/* New Request Notification */}
                {request.status === 'new' && (
                  <div className="flex items-center justify-between p-3 bg-bg-tertiary/50 rounded-xl">
                    <div>
                      <p className="font-medium text-sm">Notify Assignee</p>
                      <p className="text-xs text-text-muted">
                        {request.assignee
                          ? `Send notification to ${request.assignee.name}`
                          : 'Assign someone first to send notification'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleSendNotification('new_request')}
                      disabled={sendingNotification || !request.assignee}
                      isLoading={sendingNotification}
                    >
                      <Mail className="w-4 h-4" />
                      Send
                    </Button>
                  </div>
                )}

                {/* Plan Proposed Notification */}
                {request.status === 'plan_proposed' && (
                  <div className="flex items-center justify-between p-3 bg-violet-500/10 rounded-xl">
                    <div>
                      <p className="font-medium text-sm">Notify Client</p>
                      <p className="text-xs text-text-muted">
                        Send plan proposal notification to {request.creator.name}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleSendNotification('plan_proposed')}
                      disabled={sendingNotification}
                      isLoading={sendingNotification}
                    >
                      <Mail className="w-4 h-4" />
                      Send
                    </Button>
                  </div>
                )}

                {/* Generic Status Change Notification */}
                {request.status !== 'new' && request.status !== 'plan_proposed' && request.status !== 'converted' && request.status !== 'archived' && (
                  <div className="flex items-center justify-between p-3 bg-bg-tertiary/50 rounded-xl">
                    <div>
                      <p className="font-medium text-sm">Notify Client of Status</p>
                      <p className="text-xs text-text-muted">
                        Send status update notification to {request.creator.name}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleSendNotification('status_change')}
                      disabled={sendingNotification}
                      isLoading={sendingNotification}
                    >
                      <Mail className="w-4 h-4" />
                      Send
                    </Button>
                  </div>
                )}

                {/* Notify All Project Clients */}
                {request.status !== 'archived' && (
                  <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-xl">
                    <div>
                      <p className="font-medium text-sm">Notify All Project Clients</p>
                      <p className="text-xs text-text-muted">
                        Send status update to all clients with access to this project
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleSendNotification('notify_all_clients')}
                      disabled={sendingNotification}
                      isLoading={sendingNotification}
                    >
                      <Users className="w-4 h-4" />
                      Notify All
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Actions based on status */}
            {request.status === 'new' || request.status === 'under_review' ? (
              <div className="bg-bg-secondary rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-4">Admin Actions</h2>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => setShowPlanModal(true)}>
                    <FileText className="w-4 h-4" />
                    Propose Plan
                  </Button>
                  {request.status === 'new' && (
                    <Button variant="secondary" onClick={() => handleStatusChange('under_review')}>
                      Mark as Under Review
                    </Button>
                  )}
                </div>
              </div>
            ) : null}

            {request.status === 'approved' && !request.convertedToWorkItemId && !request.convertedToBundleId && (
              <div className="bg-bg-secondary rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-4">Ready for Work</h2>
                <p className="text-text-secondary mb-4">
                  This request has been approved. Convert it to start delivering.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleConvertToWorkItem}>
                    <ArrowRightCircle className="w-4 h-4" />
                    Convert to Work Item
                  </Button>
                  <Button variant="secondary" onClick={handleConvertToBundle}>
                    <Package className="w-4 h-4" />
                    Convert to Bundle
                  </Button>
                </div>
                <p className="text-xs text-text-muted mt-3">
                  Use a bundle for complex deliverables with multiple work items.
                </p>
              </div>
            )}

            {request.convertedWorkItem && (
              <div className="bg-accent/10 border border-accent/20 rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-2">Converted to Work Item</h2>
                <Link
                  to={`/work-items/${request.convertedWorkItem.id}`}
                  className="text-accent hover:underline"
                >
                  {request.convertedWorkItem.title}
                </Link>
              </div>
            )}

            {request.convertedBundle && (
              <div className="bg-violet-500/10 border border-violet-500/20 rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <Package className="w-5 h-5 text-violet-500" />
                  Converted to Bundle
                </h2>
                <Link
                  to={`/bundles/${request.convertedBundle.id}`}
                  className="text-accent hover:underline"
                >
                  {request.convertedBundle.title}
                </Link>
              </div>
            )}

            {/* Comments Section */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-text-secondary" />
                  <h2 className="text-lg font-semibold">Comments</h2>
                  <span className="text-sm text-text-muted">
                    ({(request.comments || []).length})
                  </span>
                </div>
                {!showCommentForm && !replyingTo && (
                  <Button
                    size="sm"
                    onClick={() => setShowCommentForm(true)}
                  >
                    <Plus className="w-4 h-4" />
                    New Comment
                  </Button>
                )}
              </div>

              {/* Reply Form */}
              {replyingTo && (
                <div className="mb-6 p-4 bg-accent/5 border border-accent/20 rounded-xl">
                  <div className="flex items-start gap-2 mb-3">
                    <CornerDownRight className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-secondary">
                        Replying to <span className="font-medium text-accent">{replyingTo.userName}</span>
                      </p>
                      <p className="text-xs text-text-muted mt-1 line-clamp-2 italic">
                        "{replyingTo.content}..."
                      </p>
                    </div>
                    <button
                      onClick={() => setReplyingTo(null)}
                      className="p-1 hover:bg-bg-tertiary rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-text-muted" />
                    </button>
                  </div>
                  <form onSubmit={handleSubmitReply}>
                    <RichTextEditor
                      content={replyContent}
                      onChange={setReplyContent}
                      placeholder="Write your reply... Use @ to mention someone"
                      projectId={request.projectId}
                      className="min-h-[100px]"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="ghost" size="sm" onClick={() => setReplyingTo(null)}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={isSubmittingComment || !replyContent.replace(/<[^>]*>/g, '').trim()}
                      >
                        {isSubmittingComment ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Reply className="w-3 h-3" />
                        )}
                        Reply
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Comment Form - shown when showCommentForm is true */}
              {showCommentForm && !replyingTo && (
                <div className="mb-6 p-4 bg-bg-tertiary/50 border border-border rounded-xl">
                  <form onSubmit={handleSubmitComment}>
                    <RichTextEditor
                      content={newComment}
                      onChange={setNewComment}
                      placeholder="Add a comment... Use @ to mention someone"
                      projectId={request.projectId}
                      className="min-h-[100px]"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowCommentForm(false);
                          setNewComment('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmittingComment || !newComment.replace(/<[^>]*>/g, '').trim()}
                      >
                        {isSubmittingComment ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Post Comment
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Comment List */}
              {(() => {
                const allComments = request.comments || [];
                const topLevelComments = allComments.filter((c) => !c.parentCommentId);
                const getReplies = (parentId: string) =>
                  allComments.filter((c) => c.parentCommentId === parentId);

                if (topLevelComments.length === 0) {
                  return <p className="text-text-muted text-center py-4">No comments yet</p>;
                }

                return (
                  <div className="space-y-4">
                    {topLevelComments.map((comment) => {
                      const replies = getReplies(comment.id);
                      return (
                        <div key={comment.id} className="space-y-2">
                          {/* Parent Comment */}
                          <div
                            className={`p-4 rounded-xl border ${
                              comment.isInternal
                                ? 'bg-amber-500/10 border-amber-500/20'
                                : 'bg-bg-tertiary border-border'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden">
                                  {comment.userAvatar ? (
                                    <img
                                      src={comment.userAvatar}
                                      alt=""
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <User className="w-4 h-4 text-accent" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{comment.userName}</p>
                                  <p className="text-xs text-text-muted">
                                    {new Date(comment.createdAt).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {comment.isInternal && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-500">
                                    Internal
                                  </span>
                                )}
                                <button
                                  onClick={() => startReply(comment)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-accent hover:bg-accent/10 rounded transition-colors"
                                >
                                  <Reply className="w-3 h-3" />
                                  Reply
                                </button>
                              </div>
                            </div>
                            {comment.contentHtml ? (
                              <HtmlRenderer html={comment.contentHtml} />
                            ) : (
                              <p className="text-text-secondary whitespace-pre-wrap">
                                {comment.content}
                              </p>
                            )}
                          </div>

                          {/* Replies */}
                          {replies.length > 0 && (
                            <div className="ml-6 space-y-2 border-l-2 border-accent/20 pl-4">
                              {replies.map((reply) => (
                                <div
                                  key={reply.id}
                                  className={`p-3 rounded-lg border ${
                                    reply.isInternal
                                      ? 'bg-amber-500/5 border-amber-500/20'
                                      : 'bg-bg-tertiary/50 border-border/50'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center overflow-hidden">
                                        {reply.userAvatar ? (
                                          <img
                                            src={reply.userAvatar}
                                            alt=""
                                            className="w-6 h-6 rounded-full object-cover"
                                          />
                                        ) : (
                                          <User className="w-3 h-3 text-accent" />
                                        )}
                                      </div>
                                      <div>
                                        <p className="font-medium text-xs">{reply.userName}</p>
                                        <p className="text-xs text-text-muted">
                                          {new Date(reply.createdAt).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {reply.isInternal && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 text-[10px]">
                                          Internal
                                        </span>
                                      )}
                                      <button
                                        onClick={() => startReply(comment)}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-text-muted hover:text-accent hover:bg-accent/10 rounded transition-colors"
                                      >
                                        <Reply className="w-2.5 h-2.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {reply.contentHtml ? (
                                    <div className="text-sm">
                                      <HtmlRenderer html={reply.contentHtml} />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-text-secondary whitespace-pre-wrap">
                                      {reply.content}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <h3 className="text-sm font-medium text-text-muted mb-3">Status</h3>
              <div className="space-y-3">
                <span
                  className="inline-block px-4 py-2 rounded-lg font-medium"
                  style={{
                    backgroundColor: statusInfo?.bgColor,
                    color: statusInfo?.color,
                  }}
                >
                  {statusInfo?.label || request.status}
                </span>

                {allowedTransitions.length > 0 && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-text-muted mb-2">Change to:</p>
                    <div className="flex flex-wrap gap-2">
                      {allowedTransitions.map((status) => {
                        const info = REQUEST_STATUSES[status];
                        return (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(status)}
                            className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-bg-tertiary transition-colors"
                          >
                            {info?.label || status}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Priority */}
            {priorityInfo && (
              <div className="bg-bg-secondary rounded-2xl p-6">
                <h3 className="text-sm font-medium text-text-muted mb-3">Priority</h3>
                <span
                  className="inline-block px-3 py-1.5 rounded-lg text-sm"
                  style={{
                    backgroundColor: priorityInfo.bgColor,
                    color: priorityInfo.color,
                  }}
                >
                  {priorityInfo.label}
                </span>
              </div>
            )}

            {/* Tags */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-text-muted">Tags</h3>
                <button
                  onClick={() => setShowTagModal(true)}
                  className="p-1 rounded hover:bg-bg-tertiary transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {request.tags.length === 0 ? (
                  <p className="text-text-muted text-sm">No tags</p>
                ) : (
                  request.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm group"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                      }}
                    >
                      {tag.name}
                      <button
                        onClick={() => handleRemoveTag(tag.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* People */}
            <div className="bg-bg-secondary rounded-2xl p-6 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-text-muted mb-2">Created by</h3>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    {request.creator.avatarUrl ? (
                      <img
                        src={request.creator.avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <User className="w-4 h-4 text-accent" />
                    )}
                  </div>
                  <span className="font-medium">{request.creator.name}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-text-muted mb-2">Assigned to</h3>
                {request.assignee ? (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      {request.assignee.avatarUrl ? (
                        <img
                          src={request.assignee.avatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    <span className="font-medium">{request.assignee.name}</span>
                  </div>
                ) : (
                  <p className="text-text-muted text-sm">Unassigned</p>
                )}
              </div>

              {request.reviewer && (
                <div>
                  <h3 className="text-sm font-medium text-text-muted mb-2">Reviewed by</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                      {request.reviewer.avatarUrl ? (
                        <img
                          src={request.reviewer.avatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="w-4 h-4 text-accent" />
                      )}
                    </div>
                    <span className="font-medium">{request.reviewer.name}</span>
                  </div>
                </div>
              )}

              {request.approver && (
                <div>
                  <h3 className="text-sm font-medium text-text-muted mb-2">Approved by</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                      {request.approver.avatarUrl ? (
                        <img
                          src={request.approver.avatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="w-4 h-4 text-accent" />
                      )}
                    </div>
                    <span className="font-medium">{request.approver.name}</span>
                  </div>
                  {request.approvedAt && (
                    <p className="text-xs text-text-muted mt-1">
                      {new Date(request.approvedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="bg-bg-secondary rounded-2xl p-6">
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <Calendar className="w-4 h-4" />
                <span>Created {new Date(request.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tag Modal */}
      {showTagModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowTagModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-bg-secondary rounded-2xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-4">Add Tag</h2>
            {unusedTags.length === 0 ? (
              <p className="text-text-muted">All tags are already added</p>
            ) : (
              <div className="space-y-2">
                {unusedTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => {
                      handleAddTag(tag.id);
                      setShowTagModal(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-bg-tertiary transition-colors"
                  >
                    <span
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span>{tag.name}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border">
              <Button variant="secondary" className="w-full" onClick={() => setShowTagModal(false)}>
                Cancel
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </PageTransition>
  );
}
